import { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { DatabaseService } from '../lib/database';
import { useClerkIntegration } from './useClerkIntegration';
import type { 
  UserRole, 
  Permission, 
  UserProfileWithPermissions,
  GetAllUsersResult,
  UserRoleUpdateRequest,
  UserCreateRequest,
  ApiResponse
} from '../types/database';

interface UseUserRoleReturn {
  // User profile data
  userProfile: UserProfileWithPermissions | null;
  userRole: UserRole | null;
  permissions: Permission[];
  isLoading: boolean;
  error: string | null;

  // Permission checking
  hasPermission: (permission: Permission) => boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  canManageUsers: boolean;
  canReadFinancialData: boolean;
  canWriteFinancialData: boolean;
  canReadDealScenarios: boolean;
  canWriteDealScenarios: boolean;
  canReadLOIDocuments: boolean;
  canWriteLOIDocuments: boolean;
  canExportData: boolean;
  canViewAnalytics: boolean;
  canManageDocuments: boolean;
  canUploadDocuments: boolean;
  canDownloadDocuments: boolean;
  canShareDocuments: boolean;
  canDeleteDocuments: boolean;
  canEditDocuments: boolean;

  // User management (admin only)
  allUsers: GetAllUsersResult[];
  refreshUsers: () => Promise<void>;
  updateUserRole: (request: UserRoleUpdateRequest) => Promise<ApiResponse<boolean>>;
  createUser: (request: UserCreateRequest) => Promise<ApiResponse<any>>;
  deactivateUser: (userId: string) => Promise<ApiResponse<{ success: boolean; message: string }>>;
  reactivateUser: (userId: string) => Promise<ApiResponse<{ success: boolean; message: string }>>;
  toggleSuperAdmin: (userId: string) => Promise<ApiResponse<{ success: boolean; message: string; is_super_admin: boolean }>>;
  deleteUser: (userId: string) => Promise<ApiResponse<{ success: boolean; message: string }>>;
  impersonateUser: (userId: string) => Promise<ApiResponse<{ 
    success: boolean; 
    token: string;
    url: string;
    user_id: string; 
    email: string; 
    name: string;
    expires_at: string;
    message: string 
  }>>;

  // Role permission management (admin only)
  rolePermissions: Record<UserRole, Permission[]>;
  refreshRolePermissions: () => Promise<void>;
  updateRolePermissions: (role: UserRole, permissions: Permission[]) => Promise<ApiResponse<boolean>>;

  // Utility functions
  refreshProfile: () => Promise<void>;
}

export function useUserRole(): UseUserRoleReturn {
  const { user, isLoaded, isSignedIn } = useUser();
  const { isSynced, isLoading: isClerkLoading, syncUserWithDatabase } = useClerkIntegration();
  const [userProfile, setUserProfile] = useState<UserProfileWithPermissions | null>(null);
  const [allUsers, setAllUsers] = useState<GetAllUsersResult[]>([]);
  const [rolePermissions, setRolePermissions] = useState<Record<UserRole, Permission[]>>({
    Admin: [],
    Lender: [],
    Seller: [],
    Viewer: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const retryCountRef = useRef(0); // Use ref instead of state to prevent infinite loop
  const isRetryingRef = useRef(false); // Prevent concurrent retry attempts
  const isLoadingRef = useRef(false); // Prevent concurrent load attempts
  const hasLoadedRef = useRef(false); // Track if we've successfully loaded once

  // Derived state
  const userRole = userProfile?.role || null;
  const permissions = userProfile?.permissions || [];
  const isAdmin = userRole === 'Admin';
  const isSuperAdmin = isAdmin && (userProfile?.metadata as any)?.is_super_admin === true;
  const canManageUsers = hasPermission('manage_users');
  const canReadFinancialData = hasPermission('read_financial_data');
  const canWriteFinancialData = hasPermission('write_financial_data');
  const canReadDealScenarios = hasPermission('read_deal_scenarios');
  const canWriteDealScenarios = hasPermission('write_deal_scenarios');
  const canReadLOIDocuments = hasPermission('read_loi_documents');
  const canWriteLOIDocuments = hasPermission('write_loi_documents');
  const canExportData = hasPermission('export_data');
  const canViewAnalytics = hasPermission('view_analytics');
  const canManageDocuments = hasPermission('manage_documents');
  const canUploadDocuments = hasPermission('upload_documents');
  const canDownloadDocuments = hasPermission('download_documents');
  const canShareDocuments = hasPermission('share_documents');
  const canDeleteDocuments = hasPermission('delete_documents');
  const canEditDocuments = hasPermission('edit_documents');

  // Permission checking function
  function hasPermission(permission: Permission): boolean {
    const hasPerm = permissions.includes(permission);
    return hasPerm;
  }

  // Load user profile
  const loadUserProfile = useCallback(async () => {
    if (!isLoaded || !isSignedIn || !user) {
      setIsLoading(false);
      return;
    }

    // Wait for Clerk integration to sync the user before loading profile
    if (!isSynced && isClerkLoading) {
      return;
    }

    // Prevent concurrent load or retry attempts
    if (isLoadingRef.current || isRetryingRef.current) {
      return;
    }

    // If we've successfully loaded before, don't reload (prevents infinite loops)
    // UNLESS permissions are missing (handles cache invalidation after code updates)
    if (hasLoadedRef.current && userProfile?.permissions && userProfile.permissions.length > 0) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setIsLoading(true);
      setError(null);

      const response = await DatabaseService.getUserProfile(user.id);
      
      if (response.error) {
        // Only retry for "User profile not found" errors, not network errors
        if (response.error === 'User profile not found' && retryCountRef.current < 3) {
          retryCountRef.current += 1;
          isRetryingRef.current = true;
          isLoadingRef.current = false; // Allow retry to proceed
          
          // Try to sync the user
          const syncResult = await syncUserWithDatabase();
          if (syncResult.success) {
            // Retry loading the profile after successful sync
            setTimeout(() => {
              isRetryingRef.current = false;
              loadUserProfile();
            }, 1000); // Increased delay to prevent hammering
            return;
          } else {
            isRetryingRef.current = false;
          }
        } else if (response.error.includes('Failed to fetch') || response.error.includes('TypeError')) {
          // Network error - don't retry, just show error
          setError('Network error. Please check your connection and refresh the page.');
        } else {
          // Other errors
          setError(response.error);
          console.error('Failed to load user profile:', response.error);
        }
      } else if (response.data) {
        setUserProfile(response.data);
        retryCountRef.current = 0; // Reset retry count on success
        hasLoadedRef.current = true; // Mark as successfully loaded
        isRetryingRef.current = false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user profile';
      setError(errorMessage);
      console.error('Unexpected error loading user profile:', err);
      isRetryingRef.current = false;
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [isLoaded, isSignedIn, user, isSynced, isClerkLoading, syncUserWithDatabase]);

  // Load all users (admin only)
  const loadAllUsers = useCallback(async () => {
    if (!isAdmin || !user) {
      return;
    }

    try {
      const response = await DatabaseService.getAllUsers(user.id);
      
      if (response.error) {
        console.error('Failed to load all users:', response.error);
      } else if (response.data) {
        setAllUsers(response.data);
      }
    } catch (err) {
      console.error('Unexpected error loading all users:', err);
    }
  }, [isAdmin, user]);

  // Load role permissions (admin only)
  const loadRolePermissions = useCallback(async () => {
    if (!isAdmin || !user) {
      return;
    }

    try {
      const response = await DatabaseService.getRolePermissions();
      
      if (response.error) {
        console.error('Failed to load role permissions:', response.error);
      } else if (response.data) {
        setRolePermissions(response.data);
      }
    } catch (err) {
      console.error('Unexpected error loading role permissions:', err);
    }
  }, [isAdmin]);

  // Update user role
  const updateUserRole = useCallback(async (request: UserRoleUpdateRequest): Promise<ApiResponse<boolean>> => {
    try {
      const response = await DatabaseService.updateUserRole(request, user?.id);
      
      if (response.data) {
        // Refresh users list after successful update
        await loadAllUsers();
      }
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user role';
      return { error: errorMessage };
    }
  }, [loadAllUsers, user?.id]);

  // Create user
  const createUser = useCallback(async (request: UserCreateRequest): Promise<ApiResponse<any>> => {
    try {
      const response = await DatabaseService.createUserProfile(request);
      
      if (response.data) {
        // Refresh users list after successful creation
        await loadAllUsers();
      }
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create user';
      return { error: errorMessage };
    }
  }, [loadAllUsers]);

  // Delete user
  // Deactivate user (admin only)
  const deactivateUser = useCallback(async (userId: string): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    try {
      if (!user?.id) {
        return { error: 'User not authenticated' };
      }

      const response = await DatabaseService.deactivateUser(userId, user.id);
      
      if (response.data) {
        // Refresh users list after successful deactivation
        await loadAllUsers();
      }
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to deactivate user';
      return { error: errorMessage };
    }
  }, [loadAllUsers, user?.id]);

  // Reactivate user (admin only)
  const reactivateUser = useCallback(async (userId: string): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    try {
      if (!user?.id) {
        return { error: 'User not authenticated' };
      }

      const response = await DatabaseService.reactivateUser(userId, user.id);
      
      if (response.data) {
        // Refresh users list after successful reactivation
        await loadAllUsers();
      }
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reactivate user';
      return { error: errorMessage };
    }
  }, [loadAllUsers, user?.id]);

  // Toggle Super Admin status (super admin only)
  const toggleSuperAdmin = useCallback(async (userId: string): Promise<ApiResponse<{ success: boolean; message: string; is_super_admin: boolean }>> => {
    try {
      if (!user?.id) {
        return { error: 'User not authenticated' };
      }

      const response = await DatabaseService.toggleSuperAdmin(userId, user.id);
      
      if (response.data) {
        // Refresh users list after successful toggle
        await loadAllUsers();
      }
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to toggle Super Admin status';
      return { error: errorMessage };
    }
  }, [loadAllUsers, user?.id]);

  // Permanently delete user (admin only)
  const deleteUser = useCallback(async (userId: string): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    try {
      if (!user?.id) {
        return { error: 'User not authenticated' };
      }

      const response = await DatabaseService.deleteUser(userId, user.id);
      
      if (response.data) {
        // Refresh users list after successful deletion
        await loadAllUsers();
      }
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      return { error: errorMessage };
    }
  }, [loadAllUsers, user?.id]);

  // Impersonate user (admin only)
  const impersonateUser = useCallback(async (userId: string): Promise<ApiResponse<{ 
    success: boolean; 
    token: string;
    url: string;
    user_id: string; 
    email: string; 
    name: string;
    expires_at: string;
    message: string 
  }>> => {
    try {
      if (!user?.id) {
        return { error: 'User not authenticated' };
      }

      const response = await DatabaseService.impersonateUser(userId, user.id);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate impersonation token';
      return { error: errorMessage };
    }
  }, [user?.id]);

  // Update role permissions
  const updateRolePermissions = useCallback(async (role: UserRole, permissions: Permission[]): Promise<ApiResponse<boolean>> => {
    try {
      const response = await DatabaseService.updateRolePermissions(role, permissions, user?.id);
      
      if (response.data) {
        // Refresh role permissions after successful update
        await loadRolePermissions();
      }
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update role permissions';
      return { error: errorMessage };
    }
  }, [loadRolePermissions, user?.id]);

  // Refresh functions
  const refreshProfile = useCallback(async () => {
    hasLoadedRef.current = false; // Allow reload
    retryCountRef.current = 0; // Reset retry count
    isLoadingRef.current = false; // Reset loading flag
    isRetryingRef.current = false; // Reset retrying flag
    await loadUserProfile();
  }, [loadUserProfile]);

  const refreshUsers = useCallback(async () => {
    await loadAllUsers();
  }, [loadAllUsers]);

  const refreshRolePermissions = useCallback(async () => {
    await loadRolePermissions();
  }, [loadRolePermissions]);

  // Reset flags when user changes
  useEffect(() => {
    if (user?.id) {
      hasLoadedRef.current = false;
      retryCountRef.current = 0;
      isLoadingRef.current = false;
      isRetryingRef.current = false;
    }
  }, [user?.id]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  useEffect(() => {
    if (isAdmin) {
      loadAllUsers();
      loadRolePermissions();
    }
  }, [isAdmin, loadAllUsers, loadRolePermissions]);

  return {
    // User profile data
    userProfile,
    userRole,
    permissions,
    isLoading,
    error,

    // Permission checking
    hasPermission,
    isAdmin,
    isSuperAdmin,
    canManageUsers,
    canReadFinancialData,
    canWriteFinancialData,
    canReadDealScenarios,
    canWriteDealScenarios,
    canReadLOIDocuments,
    canWriteLOIDocuments,
    canExportData,
    canViewAnalytics,
    canManageDocuments,
    canUploadDocuments,
    canDownloadDocuments,
    canShareDocuments,
    canDeleteDocuments,
    canEditDocuments,

    // User management
    allUsers,
    refreshUsers,
    updateUserRole,
    createUser,
    deactivateUser,
    reactivateUser,
    toggleSuperAdmin,
    deleteUser,
    impersonateUser,

    // Role permission management
    rolePermissions,
    refreshRolePermissions,
    updateRolePermissions,

    // Utility functions
    refreshProfile
  };
}

// Convenience hook for just checking permissions
export function usePermissions() {
  const { hasPermission, isAdmin, canManageUsers, canReadFinancialData, canWriteFinancialData } = useUserRole();
  
  return {
    hasPermission,
    isAdmin,
    canManageUsers,
    canReadFinancialData,
    canWriteFinancialData
  };
}

// Hook for admin-only features
export function useAdminFeatures() {
  const { 
    isAdmin,
    isSuperAdmin,
    isLoading,
    canManageUsers, 
    allUsers, 
    refreshUsers, 
    updateUserRole, 
    createUser, 
    deleteUser,
    impersonateUser,
    rolePermissions,
    refreshRolePermissions,
    updateRolePermissions
  } = useUserRole();
  
  return {
    isAdmin,
    isSuperAdmin,
    isLoading,
    canManageUsers,
    allUsers,
    refreshUsers,
    updateUserRole,
    createUser,
    deleteUser,
    impersonateUser,
    rolePermissions,
    refreshRolePermissions,
    updateRolePermissions
  };
}
